import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';
import { Save, Code, Info } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

const GoogleTagManager = () => {
  const [settings, setSettings] = useState({
    gtm_container_id: '',
    ga4_measurement_id: '',
    conversion_code: '',
    fb_pixel_id: '',
    fb_conversion_token: '',
    tiktok_pixel: '',
    custom_header_code: ''
  });
  
  const [loading, setLoading] = useState(true);
  
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/google-tag-manager`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching GTM settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/google-tag-manager`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Google Tag Manager settings updated successfully. Changes will appear on next page load.'
        });
        fetchSettings();
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating GTM settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to update Google Tag Manager settings',
        variant: 'destructive'
      });
    }
  };

  const generateGTMCode = () => {
    if (!settings.gtm_container_id) return '';
    
    return `<!-- Google Tag Manager -->
<script async src="https://www.googletagmanager.com/gtag/js?id=${settings.gtm_container_id}"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '${settings.gtm_container_id}');
</script>
<!-- End Google Tag Manager -->`;
  };

  const generateGA4Code = () => {
    if (!settings.ga4_measurement_id) return '';
    
    return `<!-- Google Analytics 4 -->
<script async src="https://www.googletagmanager.com/gtag/js?id=${settings.ga4_measurement_id}"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '${settings.ga4_measurement_id}');
</script>
<!-- End Google Analytics 4 -->`;
  };

  const generateFBPixelCode = () => {
    if (!settings.fb_pixel_id) return '';
    
    return `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${settings.fb_pixel_id}');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1" style="display:none"
src="https://www.facebook.com/tr?id=${settings.fb_pixel_id}&ev=PageView&noscript=1"
/></noscript>
<!-- End Meta Pixel Code -->`;
  };

  const generateTikTokPixelCode = () => {
    if (!settings.tiktok_pixel) return '';
    
    return `<!-- TikTok Pixel Code -->
<script>
!function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
  ttq.load('${settings.tiktok_pixel}');
  ttq.page();
}(window, document, 'ttq');
</script>
<!-- End TikTok Pixel Code -->`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-500 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Google Tag Manager</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Configure Google Tag Manager, Google Analytics 4, and conversion tracking for your website
        </p>
      </div>

      {/* Info Alert */}
      <Alert className="mb-6">
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>What is Google Tag Manager?</strong><br />
          GTM lets you manage tracking codes (Google Analytics, Facebook Pixel, etc.) without editing code. 
          Changes made here will automatically appear in your website's &lt;head&gt; section on the next page load.
        </AlertDescription>
      </Alert>

      {/* Main Settings Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Tracking Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* GTM Container ID */}
          <div className="space-y-2">
            <Label htmlFor="gtm_container_id" className="text-base font-semibold">
              TAG Manager Container ID
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Your Google Tag Manager container ID (format: GTM-XXXXXX or AW-XXXXXXXXXX)
            </p>
            <Input
              id="gtm_container_id"
              value={settings.gtm_container_id}
              onChange={(e) => setSettings({ ...settings, gtm_container_id: e.target.value })}
              placeholder="GTM-XXXXXX or AW-XXXXXXXXXX"
              className="font-mono"
            />
            {settings.gtm_container_id && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Generated Code Preview:
                </p>
                <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border overflow-x-auto">
                  {generateGTMCode()}
                </pre>
              </div>
            )}
          </div>

          {/* GA4 Measurement ID */}
          <div className="space-y-2">
            <Label htmlFor="ga4_measurement_id" className="text-base font-semibold">
              Google GA4 Measurement ID
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Your Google Analytics 4 measurement ID (format: G-XXXXXXXXXX)
            </p>
            <Input
              id="ga4_measurement_id"
              value={settings.ga4_measurement_id}
              onChange={(e) => setSettings({ ...settings, ga4_measurement_id: e.target.value })}
              placeholder="G-XXXXXXXXXX"
              className="font-mono"
            />
            {settings.ga4_measurement_id && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Generated Code Preview:
                </p>
                <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border overflow-x-auto">
                  {generateGA4Code()}
                </pre>
              </div>
            )}
          </div>

          {/* Conversion Code */}
          <div className="space-y-2">
            <Label htmlFor="conversion_code" className="text-base font-semibold">
              Conversion ID
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Add your conversion tracking ID or code here
            </p>
            <Textarea
              id="conversion_code"
              value={settings.conversion_code}
              onChange={(e) => setSettings({ ...settings, conversion_code: e.target.value })}
              placeholder="<!-- Conversion tracking code -->"
              rows={6}
              className="font-mono text-sm"
            />
          </div>

          {/* Facebook Pixel ID */}
          <div className="space-y-2">
            <Label htmlFor="fb_pixel_id" className="text-base font-semibold">
              FB Pixel Tracking ID
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Your Facebook Pixel ID (numeric ID, e.g., 1234567890)
            </p>
            <Input
              id="fb_pixel_id"
              value={settings.fb_pixel_id}
              onChange={(e) => setSettings({ ...settings, fb_pixel_id: e.target.value })}
              placeholder="1234567890"
              className="font-mono"
            />
            {settings.fb_pixel_id && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Generated Code Preview:
                </p>
                <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border overflow-x-auto max-h-40">
                  {generateFBPixelCode()}
                </pre>
              </div>
            )}
          </div>

          {/* Facebook Conversion API Access Token */}
          <div className="space-y-2">
            <Label htmlFor="fb_conversion_token" className="text-base font-semibold">
              FB Conversion API Access Token
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Your Facebook Conversion API access token (optional)
            </p>
            <Textarea
              id="fb_conversion_token"
              value={settings.fb_conversion_token}
              onChange={(e) => setSettings({ ...settings, fb_conversion_token: e.target.value })}
              placeholder="Enter your Facebook Conversion API access token"
              rows={3}
              className="font-mono text-sm"
            />
          </div>

          {/* TikTok Pixel */}
          <div className="space-y-2">
            <Label htmlFor="tiktok_pixel" className="text-base font-semibold">
              Tiktok PIXEL
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Your TikTok Pixel ID (format: CXXXXXXXXXX)
            </p>
            <Input
              id="tiktok_pixel"
              value={settings.tiktok_pixel}
              onChange={(e) => setSettings({ ...settings, tiktok_pixel: e.target.value })}
              placeholder="CXXXXXXXXXX"
              className="font-mono"
            />
            {settings.tiktok_pixel && (
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded border">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Generated Code Preview:
                </p>
                <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border overflow-x-auto max-h-40">
                  {generateTikTokPixelCode()}
                </pre>
              </div>
            )}
          </div>

          {/* Custom Code Header */}
          <div className="space-y-2">
            <Label htmlFor="custom_header_code" className="text-base font-semibold">
              Custom Code Header
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Add any custom header scripts or tracking codes here
            </p>
            <Textarea
              id="custom_header_code"
              value={settings.custom_header_code}
              onChange={(e) => setSettings({ ...settings, custom_header_code: e.target.value })}
              placeholder="<script type='text/javascript'>
  /* Your custom code here */
</script>"
              rows={8}
              className="font-mono text-sm"
            />
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t">
            <Button onClick={handleSave} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              Save Configuration
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle>How to Get Your IDs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">📦 GTM Container ID:</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>Go to <a href="https://tagmanager.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Tag Manager</a></li>
              <li>Select your container</li>
              <li>Find your Container ID in the top-right corner (e.g., GTM-XXXXXX)</li>
            </ol>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2">📊 GA4 Measurement ID:</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Analytics</a></li>
              <li>Select your property → Admin → Data Streams</li>
              <li>Click on your web stream</li>
              <li>Copy the Measurement ID (e.g., G-XXXXXXXXXX)</li>
            </ol>
          </div>

          <div>
            <h3 className="font-semibold mb-2">🎯 Conversion Code:</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Add your conversion tracking ID or code for tracking conversions from ads or campaigns.
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-2">📘 Facebook Pixel ID:</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>Go to <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Facebook Events Manager</a></li>
              <li>Select your Pixel</li>
              <li>Copy the Pixel ID (numeric, e.g., 1234567890)</li>
            </ol>
          </div>

          <div>
            <h3 className="font-semibold mb-2">🔑 Facebook Conversion API:</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Optional access token for server-side conversion tracking. Find this in your Facebook Events Manager under "Settings" → "Conversions API" → "Generate Access Token".
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-2">🎵 TikTok Pixel:</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>Go to <a href="https://ads.tiktok.com/i18n/events_manager" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">TikTok Events Manager</a></li>
              <li>Select your Pixel</li>
              <li>Copy the Pixel ID (format: CXXXXXXXXXX)</li>
            </ol>
          </div>

          <div>
            <h3 className="font-semibold mb-2">💻 Custom Code Header:</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Add any additional tracking codes or custom scripts here. These will be injected into your website's &lt;head&gt; section.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleTagManager;
